/**
 * THE 12 SIGNATURES — tingl's proprietary emotional language.
 * No emojis. Each vibe is a living audiovisual signature:
 * form + motion + sound + touch, engineered as one identity.
 */

export interface Vibe {
  key: string
  label: string
  word: string           // shown on detonation
  color: string
  bg: string
  glyph: string          // minimal protocol glyph (lock screen only)
  haptic: number[]
  phrases: string[]
}

export const VIBES: Record<string, Vibe> = {
  LOVE: {
    key: 'LOVE', label: 'Love', word: 'LOVE',
    color: '#FF4D8D', bg: 'rgba(255,77,141,0.20)', glyph: '♥',
    haptic: [90, 120, 90],
    phrases: ['just because', 'you are loved', 'always', 'no reason needed', 'endlessly'],
  },
  HELD: {
    key: 'HELD', label: 'Held', word: 'HELD',
    color: '#FFB347', bg: 'rgba(255,179,71,0.18)', glyph: '◠',
    haptic: [400, 80, 400],
    phrases: ['i got you', 'arms around you', "you're safe here", 'lean on me', 'not letting go'],
  },
  I_SEE_YOU: {
    key: 'I_SEE_YOU', label: 'I See You', word: 'I SEE YOU',
    color: '#9B5CFF', bg: 'rgba(155,92,255,0.18)', glyph: '◉',
    haptic: [250, 150, 60],
    phrases: ['i see you', "you're not invisible", 'i notice', 'felt, not said', 'right here'],
  },
  PROUD: {
    key: 'PROUD', label: 'Proud', word: 'PROUD OF YOU',
    color: '#FFD34D', bg: 'rgba(255,211,77,0.16)', glyph: '▲',
    haptic: [70, 40, 70, 40, 200],
    phrases: ['so proud of you', 'look at you', 'you did that', 'knew you could', 'beaming'],
  },
  SPARK: {
    key: 'SPARK', label: 'Spark', word: 'SPARK',
    color: '#C8FF00', bg: 'rgba(200,255,0,0.16)', glyph: 'ϟ',
    haptic: [30, 20, 30, 20, 90],
    phrases: ['you lit this up', 'electric', 'instant energy', 'that was you', 'alive rn'],
  },
  HYPE: {
    key: 'HYPE', label: 'Hype', word: 'MASSIVE W',
    color: '#FF5A3C', bg: 'rgba(255,90,60,0.20)', glyph: '✦',
    haptic: [40, 20, 40, 20, 40, 20, 180],
    phrases: ['W', 'absolutely massive', 'no notes', 'go crazy', 'certified win'],
  },
  LOCKED_IN: {
    key: 'LOCKED_IN', label: 'Locked In', word: 'LOCKED IN',
    color: '#00E5FF', bg: 'rgba(0,229,255,0.16)', glyph: '◎',
    haptic: [500, 60, 40],
    phrases: ['go get it', 'tunnel vision', 'you got this', 'nothing else matters', 'full send'],
  },
  AURA: {
    key: 'AURA', label: 'Aura', word: 'AURA',
    color: '#C08CFF', bg: 'rgba(192,140,255,0.18)', glyph: '◯',
    haptic: [60, 60, 90, 60, 140],
    phrases: ['+1000 aura', 'main character', 'unmatched energy', 'glowing rn', "it's giving legend"],
  },
  MISS_YOU: {
    key: 'MISS_YOU', label: 'Miss You', word: 'MISS YOU',
    color: '#B8A9FF', bg: 'rgba(184,169,255,0.16)', glyph: '∴',
    haptic: [80, 300, 80],
    phrases: ['wish you were here', 'come back', 'the void noticed', 'not the same without you', 'soon?'],
  },
  THREE_AM: {
    key: 'THREE_AM', label: '3AM', word: '3AM',
    color: '#4D7CFF', bg: 'rgba(77,124,255,0.16)', glyph: '☾',
    haptic: [700],
    phrases: ['you crossed my mind', "can't sleep, thought of you", 'quiet hours', 'still thinking', 'the night knows'],
  },
  DEAD: {
    key: 'DEAD', label: 'Dead', word: "I'M DEAD",
    color: '#F2F2F2', bg: 'rgba(242,242,242,0.10)', glyph: '▬',
    haptic: [200, 250, 30, 30, 30, 30, 120],
    phrases: ["i'm deceased", 'you killed me', 'crying rn', 'NOT you doing this', 'gone. bye.'],
  },
  CHAOS: {
    key: 'CHAOS', label: 'Chaos', word: 'CHAOS',
    color: '#FF2DD4', bg: 'rgba(255,45,212,0.18)', glyph: '▞',
    haptic: [30, 30, 60, 20, 30, 40, 80, 20, 30],
    phrases: ['unhinged (affectionate)', 'what is happening', 'feral hours', 'no thoughts', 'beautiful disaster'],
  },
  BIRTHDAY: {
    key: 'BIRTHDAY', label: 'Birthday', word: 'HAPPY BIRTHDAY',
    color: '#FF6BD6', bg: 'rgba(255,107,214,0.18)', glyph: '✶',
    haptic: [60, 40, 60, 40, 60, 40, 220],
    phrases: ['happy birthday!', 'celebrate you today', 'another year of magic'],
  },
  THANK_YOU: {
    key: 'THANK_YOU', label: 'Thank You', word: 'THANK YOU',
    color: '#FFB347', bg: 'rgba(255,179,71,0.18)', glyph: '✿',
    haptic: [180, 90, 320],
    phrases: ['thank you', "you didn't have to — but you did", 'grateful for you'],
  },
  CONGRATS: {
    key: 'CONGRATS', label: 'Congrats', word: 'CONGRATS',
    color: '#FFD34D', bg: 'rgba(255,211,77,0.18)', glyph: '❋',
    haptic: [50, 30, 50, 30, 50, 30, 260],
    phrases: ['congratulations!', 'so deserved', 'you did that'],
  },
  ANNIVERSARY: {
    key: 'ANNIVERSARY', label: 'Anniversary', word: 'HAPPY ANNIVERSARY',
    color: '#FF8DA1', bg: 'rgba(255,141,161,0.18)', glyph: '∞',
    haptic: [140, 100, 140, 100, 400],
    phrases: ['happy anniversary', 'still choosing you', 'every year, luckier'],
  },
  KNOCK: {
    key: 'KNOCK', label: 'Knock', word: 'KNOCK KNOCK',
    color: '#00FFD1', bg: 'rgba(0,255,209,0.16)', glyph: '✦',
    haptic: [40, 60, 40, 300, 40, 60, 40],
    phrases: ['someone wants into your orbit'],
  },
}

// KNOCK is a system signature — never in the send grid
const OCCASION_KEYS = ['BIRTHDAY', 'THANK_YOU', 'CONGRATS', 'ANNIVERSARY']
export const VIBE_LIST = Object.values(VIBES).filter((v) => v.key !== 'KNOCK')
export const FEELING_LIST = VIBE_LIST.filter((v) => !OCCASION_KEYS.includes(v.key))
export const OCCASION_LIST = VIBE_LIST.filter((v) => OCCASION_KEYS.includes(v.key))

// Old vibe keys from earlier tingls map to the new language
const LEGACY: Record<string, string> = {
  HUG: 'HELD', FIRE: 'HYPE', BREATHE: 'LOCKED_IN', FLOWING: 'AURA',
  MAGIC: 'AURA', I_CARE: 'THREE_AM', YOU_GOT_THIS: 'PROUD',
}

export function resolveVibe(key: string): Vibe {
  return VIBES[key] || VIBES[LEGACY[key]] || VIBES.LOVE
}

export const getPhrase = (vibeKey: string): string => {
  const v = resolveVibe(vibeKey)
  return v.phrases[Math.floor(Math.random() * v.phrases.length)]
}

// ─── FLAVORS — calibrate the feeling without typing ──────────────────────────
// Same signature, three registers. Sender picks the flavor in one tap.
export interface Flavor { key: string; label: string; phrase: string; intensity: number }

export const FLAVORS: Record<string, Flavor[]> = {
  LOVE: [
    { key: 'soft',    label: 'just because', phrase: 'just because' , intensity: 0.68 },
    { key: 'warm',    label: 'you matter',   phrase: 'you matter to me' , intensity: 1.0 },
    { key: 'charged', label: 'you know',     phrase: 'you know what this is' , intensity: 1.45 },
  ],
  HELD: [
    { key: 'soft',    label: 'i got you',   phrase: 'i got you' , intensity: 0.68 },
    { key: 'warm',    label: 'not alone',   phrase: "you're not alone in this" , intensity: 1.0 },
    { key: 'charged', label: 'come here',   phrase: 'come here' , intensity: 1.45 },
  ],
  I_SEE_YOU: [
    { key: 'soft',    label: 'i notice',    phrase: 'i see you' , intensity: 0.68 },
    { key: 'warm',    label: 'i understand',phrase: 'i understand more than you think' , intensity: 1.0 },
    { key: 'charged', label: 'caught you',  phrase: 'i see exactly what you did' , intensity: 1.45 },
  ],
  PROUD: [
    { key: 'soft',    label: 'nice one',    phrase: 'nice one' , intensity: 0.68 },
    { key: 'warm',    label: 'so proud',    phrase: 'so proud of you' , intensity: 1.0 },
    { key: 'charged', label: 'legend',      phrase: 'absolute legend' , intensity: 1.45 },
  ],
  SPARK: [
    { key: 'soft',    label: 'a little',    phrase: 'you sparked something' , intensity: 0.68 },
    { key: 'warm',    label: 'electric',    phrase: 'electric' , intensity: 1.0 },
    { key: 'charged', label: 'combustible', phrase: "we're combustible" , intensity: 1.45 },
  ],
  HYPE: [
    { key: 'soft',    label: 'lets go',     phrase: "let's go" , intensity: 0.68 },
    { key: 'warm',    label: 'massive W',   phrase: 'massive W' , intensity: 1.0 },
    { key: 'charged', label: 'GO CRAZY',    phrase: 'GO ABSOLUTELY CRAZY' , intensity: 1.45 },
  ],
  LOCKED_IN: [
    { key: 'soft',    label: 'focus',       phrase: 'focus' , intensity: 0.68 },
    { key: 'warm',    label: 'you got this',phrase: 'you got this' , intensity: 1.0 },
    { key: 'charged', label: 'full send',   phrase: 'full send, no thoughts' , intensity: 1.45 },
  ],
  AURA: [
    { key: 'soft',    label: 'glowing',     phrase: "you're glowing" , intensity: 0.68 },
    { key: 'warm',    label: '+1000 aura',  phrase: '+1000 aura' , intensity: 1.0 },
    { key: 'charged', label: 'untouchable', phrase: 'simply untouchable' , intensity: 1.45 },
  ],
  MISS_YOU: [
    { key: 'soft',    label: 'thinking',    phrase: 'thinking of you' , intensity: 0.68 },
    { key: 'warm',    label: 'miss you',    phrase: 'i miss you' , intensity: 1.0 },
    { key: 'charged', label: 'come back',   phrase: 'come back already' , intensity: 1.45 },
  ],
  THREE_AM: [
    { key: 'soft',    label: 'you crossed my mind', phrase: 'you crossed my mind' , intensity: 0.68 },
    { key: 'warm',    label: "can't sleep", phrase: "can't sleep, thinking of you" , intensity: 1.0 },
    { key: 'charged', label: 'awake?',      phrase: 'you up?' , intensity: 1.45 },
  ],
  DEAD: [
    { key: 'soft',    label: 'lol',         phrase: 'lol' , intensity: 0.68 },
    { key: 'warm',    label: "i'm dead",    phrase: "i'm deceased" , intensity: 1.0 },
    { key: 'charged', label: 'GONE',        phrase: 'you KILLED me, goodbye' , intensity: 1.45 },
  ],
  CHAOS: [
    { key: 'soft',    label: 'a mess',      phrase: 'a beautiful mess' , intensity: 0.68 },
    { key: 'warm',    label: 'unhinged',    phrase: 'unhinged (affectionate)' , intensity: 1.0 },
    { key: 'charged', label: 'FERAL',       phrase: 'absolutely feral rn' , intensity: 1.45 },
  ],
}

export function getFlavors(vibeKey: string): Flavor[] {
  return FLAVORS[vibeKey] || [{ key: 'default', label: '', phrase: getPhrase(vibeKey), intensity: 1.0 }]
}
